/**
 * TrafficJunky click-tracking utility — RELOAD Nutrition
 *
 * Captures the TrafficJunky click id (`aclid`) when a visitor arrives from a
 * TrafficJunky / Pornhub ad, persists it first-party across the full session
 * and checkout journey, and appends it to the Shopify checkout link as a cart
 * attribute so it is saved on the order and returned by the orders/paid webhook.
 *
 * NOTE: this file never fires the conversion. The purchase postback is fired
 * server-side only after Shopify confirms a paid order (see backend).
 */

const ACLID_KEY = 'reload_tj_aclid';
const COOKIE_DAYS = 30;

// ─── Cookie helpers (first-party) ─────────────────────────────────────────────

function setCookie(name, value, days) {
  try {
    const expires = new Date(Date.now() + days * 864e5).toUTCString();
    document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/; SameSite=Lax`;
  } catch {
    /* document unavailable — skip */
  }
}

function getCookie(name) {
  try {
    const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
    return match ? decodeURIComponent(match[1]) : null;
  } catch {
    return null;
  }
}

// ─── Capture ───────────────────────────────────────────────────────────────

/**
 * Reads `aclid` from the current URL and persists it (localStorage + cookie).
 * Call once on app init. Only overwrites when a fresh aclid is present, so a
 * later navigation without the param does not wipe the stored click id.
 */
export function captureAclid() {
  if (typeof window === 'undefined') return;
  try {
    const aclid = new URLSearchParams(window.location.search).get('aclid');
    if (aclid) {
      localStorage.setItem(ACLID_KEY, aclid);
      setCookie(ACLID_KEY, aclid, COOKIE_DAYS);
    }
  } catch {
    /* storage unavailable — skip */
  }
}

/**
 * Returns the stored TrafficJunky click id, or null if none was captured.
 * Reads localStorage first, falling back to the first-party cookie.
 */
export function getAclid() {
  try {
    return localStorage.getItem(ACLID_KEY) || getCookie(ACLID_KEY);
  } catch {
    return getCookie(ACLID_KEY);
  }
}

// ─── Checkout handoff ────────────────────────────────────────────────────────

/**
 * Appends the stored aclid to a Shopify checkout URL as a cart attribute.
 * Shopify stores `attributes[aclid]` on the order as a note attribute, which
 * the orders/paid webhook returns to the backend for attribution.
 */
export function appendAclidToUrl(url) {
  const aclid = getAclid();
  if (!aclid) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}attributes[aclid]=${encodeURIComponent(aclid)}`;
}
